const INVALID_FACT_VALUE = /^(?:n\/a|na|none|not provided|not found|null|nil|unknown|\(none\)|-+)$/i;
const BOOLEAN_FACT_VALUE = /^(?:yes|no|y|n|true|false)$/i;
const EMAIL_FACT_VALUE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const EMAIL_PATTERN_SOURCE = "[A-Z0-9._%+-]+\\s*@\\s*[A-Z0-9.-]+\\s*\\.\\s*[A-Z]{2,}";
const PHONE_PATTERN_SOURCE = "(\\+?\\d[\\d\\s().-]{6,}\\d)";
const EMAIL_EXTRACT_PATTERN = new RegExp(EMAIL_PATTERN_SOURCE, "i");
const EMAIL_EXTRACT_PATTERN_GLOBAL = new RegExp(EMAIL_PATTERN_SOURCE, "ig");
const PHONE_EXTRACT_PATTERN = new RegExp(PHONE_PATTERN_SOURCE, "i");
const PHONE_EXTRACT_PATTERN_GLOBAL = new RegExp(PHONE_PATTERN_SOURCE, "ig");
const ADDRESSY_NAME_NOISE = /\b(?:road|street|st|avenue|ave|drive|dr|lane|ln|way|close|place|crescent|terrace|park|house|flat|postcode|city|town)\b/i;
const COUNTRYISH_NAME_NOISE = /\b(?:united kingdom|scotland|england|wales|northern ireland)\b/i;

const CONTEXT_ALIASES = {
  agent: "agent_details",
  agent_details: "agent_details",
  applicant: "applicant_details",
  applicant_details: "applicant_details",
  site: "site_address",
  site_address: "site_address",
  site_address_details: "site_address",
  site_details: "site_address",
};

const CONTEXT_GROUP_FALLBACK = {
  agent_details: "agent",
  applicant_details: "applicant",
  site_address: "site",
};

const normalizeHintText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeContextId = (value) => {
  const normalized = normalizeHintText(value).replace(/\s+/g, "_");
  if (!normalized) return "";
  return CONTEXT_ALIASES[normalized] || normalized;
};

const normalizeGroupId = (value) => normalizeHintText(value).replace(/\s+/g, "_");

const normalizeEmailValue = (value) => cleanFactValue(value).replace(/\s*@\s*/g, "@").replace(/\s*\.\s*/g, ".");

const extractEmailValue = (value) => {
  const match = String(value ?? "").match(EMAIL_EXTRACT_PATTERN);
  if (!match) return "";
  return normalizeEmailValue(match[0]);
};

const looksLikeEmail = (value) => EMAIL_FACT_VALUE.test(normalizeEmailValue(value));

const extractPhoneValue = (value) => {
  const match = String(value ?? "").match(PHONE_EXTRACT_PATTERN);
  if (!match) return "";
  return cleanFactValue(match[1]);
};

const looksLikePhone = (value) => Boolean(extractPhoneValue(value));

const sanitizeNameValue = (value) => {
  const normalized = cleanFactValue(value);
  if (!normalized) return "";

  let cleaned = normalized
    .replace(EMAIL_EXTRACT_PATTERN_GLOBAL, " ")
    .replace(PHONE_EXTRACT_PATTERN_GLOBAL, " ")
    .replace(/\s+/g, " ")
    .trim();

  cleaned = cleaned
    .replace(/^[,;:/-]+/, "")
    .replace(/[,;:/-]+$/, "")
    .trim();

  // Collapse repeated name fragments like "Ben Ingle Ben Ingle".
  cleaned = cleaned.replace(/\b([A-Za-z][A-Za-z'.-]*(?:\s+[A-Za-z][A-Za-z'.-]*){0,2})\s+\1\b/gi, "$1");
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  if (!cleaned) return "";
  if (/,/.test(cleaned) && (ADDRESSY_NAME_NOISE.test(cleaned) || COUNTRYISH_NAME_NOISE.test(cleaned))) return "";
  if (/\d/.test(cleaned) && ADDRESSY_NAME_NOISE.test(cleaned)) return "";
  if (cleaned.length < 2) return "";
  return cleaned;
};

const normalizeSubmittedBy = (value) => {
  const cleaned = cleanFactValue(value);
  if (!cleaned) return "";
  const normalized = cleaned.toLowerCase();
  if (normalized.includes("agent")) return "Agent";
  if (normalized.includes("applicant")) return "Applicant";
  return cleaned;
};

const normalizeBooleanish = (value) => {
  const cleaned = cleanFactValue(value);
  if (!cleaned) return "";
  if (/^(?:yes|y|true)$/i.test(cleaned)) return "Yes";
  if (/^(?:no|n|false)$/i.test(cleaned)) return "No";
  return cleaned;
};

const PROPOSAL_TEXT_IN_SITE_PATTERN =
  /\b(?:construction of|erection of|change of use|extension(?:s)? to|demolition of|works? to|proposed(?: development)?|variation sought)\b/i;

const sanitizeSiteFallback = (value, proposal = "") => {
  const cleaned = collapseRepeatedCommaBlocks(value);
  if (!cleaned) return "";

  const proposalSnippet = cleanFactValue(proposal);
  if (proposalSnippet.length >= 24) {
    const snippet = proposalSnippet.slice(0, 48).toLowerCase();
    const embeddedIdx = cleaned.toLowerCase().indexOf(snippet);
    if (embeddedIdx > 0) {
      const trimmed = cleanFactValue(cleaned.slice(0, embeddedIdx).replace(/[,\s]+$/, ""));
      if (trimmed) return trimmed;
    }
  }

  const marker = cleaned.match(PROPOSAL_TEXT_IN_SITE_PATTERN);
  if (!marker || marker.index === undefined || marker.index <= 0) {
    return collapseRepeatedCommaBlocks(cleaned);
  }

  const trimmed = cleanFactValue(cleaned.slice(0, marker.index).replace(/[,\s]+$/, ""));
  return collapseRepeatedCommaBlocks(trimmed || cleaned);
};

const hasUsableFactValue = (value) => {
  const cleaned = cleanFactValue(value);
  if (!cleaned) return false;
  return !INVALID_FACT_VALUE.test(cleaned);
};

const extractFieldValue = (field) =>
  cleanFactValue(field?.value || field?.answer_normalized || field?.answer_raw);

const fieldHintText = (field) =>
  normalizeHintText(
    [
      field?.question_contextual,
      field?.question,
      field?.label,
      field?.field_key,
      field?.key,
      field?.context_label,
      field?.context_id,
      field?.context_id_raw,
      field?.group,
      field?.group_id,
    ]
      .filter(Boolean)
      .join(" ")
  );

const matchesContext = (field, contextIds) => {
  if (!Array.isArray(contextIds) || !contextIds.length) return true;
  const wanted = contextIds.map(normalizeContextId).filter(Boolean);
  if (!wanted.length) return true;

  const observed = [
    field?.context_id,
    field?.context_id_raw,
    field?.context_label,
    field?.group_id,
  ]
    .map(normalizeContextId)
    .filter(Boolean);

  if (observed.some((id) => wanted.includes(id))) return true;

  const fieldGroup = normalizeGroupId(field?.group);
  return wanted.some((wantedId) => CONTEXT_GROUP_FALLBACK[wantedId] === fieldGroup);
};

const hintHasAnyToken = (hint, tokens = []) => {
  if (!tokens.length) return true;
  const normalizedTokens = tokens.map(normalizeHintText).filter(Boolean);
  if (!normalizedTokens.length) return true;
  return normalizedTokens.some((token) => hint.includes(token));
};

const valuePassesRequirements = (value, hint, options = {}) => {
  if (options.requiredType === "email" && !looksLikeEmail(value)) return false;
  if (options.requiredType === "phone" && !looksLikePhone(value)) return false;
  if (options.requiredType === "boolean" && !BOOLEAN_FACT_VALUE.test(cleanFactValue(value))) return false;
  if (Array.isArray(options.requireAnyHintTokens) && options.requireAnyHintTokens.length) {
    if (!hintHasAnyToken(hint, options.requireAnyHintTokens)) return false;
  }
  if (typeof options.acceptValue === "function" && !options.acceptValue(value)) return false;
  return true;
};

const scoreFieldCandidate = (field, options = {}) => {
  const value = extractFieldValue(field);
  if (!hasUsableFactValue(value)) {
    return { score: Number.NEGATIVE_INFINITY, value: "", field };
  }

  const hint = fieldHintText(field);
  if (!valuePassesRequirements(value, hint, options)) {
    return { score: Number.NEGATIVE_INFINITY, value: "", field };
  }
  let score = 0;

  if (Array.isArray(options.contextIds) && options.contextIds.length) {
    score += matchesContext(field, options.contextIds) ? 8 : -5;
  }

  if (Array.isArray(options.groupIds) && options.groupIds.length) {
    const fieldGroup = normalizeGroupId(field?.group);
    if (options.groupIds.map(normalizeGroupId).includes(fieldGroup)) score += 4;
  }

  for (const phrase of options.phrases || []) {
    const normalizedPhrase = normalizeHintText(phrase);
    if (normalizedPhrase && hint.includes(normalizedPhrase)) score += 5;
  }

  for (const token of options.tokens || []) {
    const normalizedToken = normalizeHintText(token);
    if (normalizedToken && hint.includes(normalizedToken)) score += 2;
  }

  for (const token of options.excludeTokens || []) {
    const normalizedToken = normalizeHintText(token);
    if (normalizedToken && hint.includes(normalizedToken)) score -= 4;
  }

  if (options.preferLongText && value.length >= 32) score += 3;
  if (options.preferShortText && value.length <= 24) score += 2;
  if (options.preferBoolean) score += BOOLEAN_FACT_VALUE.test(value) ? 3 : -2;
  if (options.preferEmail) score += looksLikeEmail(value) ? 4 : -3;
  if (options.preferPhone) score += looksLikePhone(value) ? 3 : -2;

  if (Number.isFinite(options.minLength) && value.length < options.minLength) score -= 3;
  if (Number.isFinite(options.maxLength) && value.length > options.maxLength) score -= 2;

  if (typeof options.valueTest === "function") {
    score += options.valueTest(value, field) ? 3 : 0;
  }

  return { score, value, field };
};

const findStrictFactValue = (fields, predicate) => {
  if (!Array.isArray(fields)) return "";
  for (const field of fields) {
    if (!field || typeof field !== "object") continue;
    if (!predicate(field)) continue;
    const value = extractFieldValue(field);
    if (hasUsableFactValue(value)) return value;
  }
  return "";
};

export const cleanFactValue = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const normalizeComparableValue = (value) =>
  cleanFactValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const dedupeFactParts = (parts = []) => {
  const resolved = [];
  const resolvedNormalized = [];

  for (const part of parts) {
    const cleaned = cleanFactValue(part);
    if (!cleaned) continue;
    const normalized = normalizeComparableValue(cleaned);
    if (!normalized) continue;

    const duplicateIdx = resolvedNormalized.findIndex(
      (existing) =>
        existing === normalized ||
        existing.includes(normalized) ||
        normalized.includes(existing)
    );

    if (duplicateIdx >= 0) {
      if (normalized.length > resolvedNormalized[duplicateIdx].length) {
        resolved[duplicateIdx] = cleaned;
        resolvedNormalized[duplicateIdx] = normalized;
      }
      continue;
    }

    resolved.push(cleaned);
    resolvedNormalized.push(normalized);
  }

  return resolved;
};

const joinDistinctFactParts = (parts = [], separator = ", ") => dedupeFactParts(parts).join(separator);

const collapseRepeatedCommaBlocks = (value) => {
  const cleaned = cleanFactValue(value);
  if (!cleaned) return "";

  const segments = cleaned
    .split(",")
    .map((segment) => cleanFactValue(segment))
    .filter(Boolean);

  if (segments.length < 4) return cleaned;

  const normalizedSegments = segments.map(normalizeComparableValue);
  for (let blockSize = 2; blockSize <= Math.floor(normalizedSegments.length / 2); blockSize += 1) {
    const block = normalizedSegments.slice(0, blockSize);
    let cursor = blockSize;
    let repeated = true;

    while (cursor + blockSize <= normalizedSegments.length) {
      for (let idx = 0; idx < blockSize; idx += 1) {
        if (normalizedSegments[cursor + idx] !== block[idx]) {
          repeated = false;
          break;
        }
      }
      if (!repeated) break;
      cursor += blockSize;
    }

    if (repeated && cursor >= blockSize * 2) {
      return segments.slice(0, blockSize).join(", ");
    }
  }

  return joinDistinctFactParts(segments, ", ") || cleaned;
};

export const truncateFactValue = (value, max = 120) => {
  const normalized = cleanFactValue(value);
  if (!normalized || normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 3).trimEnd()}...`;
};

export const findBestFieldMatch = (fields, options = {}) => {
  if (!Array.isArray(fields) || !fields.length) return null;
  const minScore = Number.isFinite(options.minScore) ? options.minScore : 3;

  let best = null;
  for (const field of fields) {
    const candidate = scoreFieldCandidate(field, options);
    if (!best) {
      best = candidate;
      continue;
    }
    if (candidate.score > best.score) {
      best = candidate;
      continue;
    }
    if (candidate.score === best.score && candidate.value.length > best.value.length) {
      best = candidate;
    }
  }

  if (!best || best.score < minScore) return null;
  return best;
};

export const findBestFactValue = (fields, options = {}) => {
  const match = findBestFieldMatch(fields, options);
  return match?.value || "";
};

export const findContextValue = (fields, contextId, questionPattern, fieldKeyPattern, options = {}) => {
  if (Array.isArray(fields)) {
    for (const field of fields) {
      if (!field || typeof field !== "object") continue;
      if (!matchesContext(field, [contextId])) continue;
      const qText = String(field?.question_contextual || field?.question || field?.label || "").toLowerCase();
      const keyText = String(field?.field_key || field?.key || "").toLowerCase();
      const strictMatch =
        (questionPattern && questionPattern.test(qText)) ||
        (fieldKeyPattern && fieldKeyPattern.test(keyText));
      if (!strictMatch) continue;
      const value = extractFieldValue(field);
      const hint = fieldHintText(field);
      if (!hasUsableFactValue(value)) continue;
      if (!valuePassesRequirements(value, hint, options)) continue;
      return value;
    }
  }

  return findBestFactValue(fields, {
    contextIds: [contextId],
    ...options,
  });
};

export const composePersonName = (fields, contextId) => {
  const roleToken = contextId === "agent_details" ? "agent" : "applicant";

  const strictName = findContextValue(fields, contextId, /\bname\b/, /(?:^|[._])(name|full_name)$/, {
    tokens: ["name", roleToken],
    requireAnyHintTokens: ["name", "full name", "name of"],
    excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
    minLength: 2,
    maxLength: 160,
    minScore: 4,
  });

  const otherTitle = findContextValue(fields, contextId, /\bother\s+title\b/, /\.other_title$/, {
    tokens: ["other", "title", roleToken],
    requireAnyHintTokens: ["title", "other title", "salutation"],
    excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
    minScore: 4,
  });
  const rawTitle = findContextValue(fields, contextId, /\btitle\b|salutation/, /\.title$/, {
    tokens: ["title", "salutation", roleToken],
    requireAnyHintTokens: ["title", "salutation"],
    excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
    preferShortText: true,
    maxLength: 30,
    minScore: 3,
  });
  const normalizedRawTitle = cleanFactValue(rawTitle).toLowerCase();
  const title = otherTitle || (normalizedRawTitle === "other" ? "" : rawTitle);

  const firstName = findContextValue(fields, contextId, /\bfirst\s+name\b/, /\.(first_name|firstname)$/, {
    tokens: ["first", "name", roleToken],
    requireAnyHintTokens: ["name", "first name", "forename", "given name"],
    excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
    preferShortText: true,
    maxLength: 50,
    minScore: 4,
  });
  const surname = findContextValue(fields, contextId, /\blast\s+name\b|\bsurname\b/, /\.(last_name|surname)$/, {
    tokens: ["last", "name", "surname", roleToken],
    requireAnyHintTokens: ["name", "last name", "surname", "family name"],
    excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
    preferShortText: true,
    maxLength: 60,
    minScore: 4,
  });
  const company = findContextValue(
    fields,
    contextId,
    /\bcompany\b|\borganisation\b|\borganization\b|\borg\b|corporate entity/,
    /\.(company|company_organisation|organisation|organization|corporate_entity|org)$/,
    {
      tokens: ["company", "organisation", "organization", "org", "corporate", "entity", roleToken],
      requireAnyHintTokens: ["company", "organisation", "organization", "org", "corporate", "entity"],
      excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
      maxLength: 140,
      minScore: 4,
    }
  );
  const fullName = findBestFactValue(fields, {
    contextIds: [contextId],
    phrases: [`${roleToken} name`, `name of ${roleToken}`],
    tokens: [roleToken, "name"],
    requireAnyHintTokens: ["name", "first name", "surname", "last name"],
    excludeTokens: ["email", "phone", "telephone", "mobile", "address", "street", "postcode", "post code", "town", "city", "fax"],
    minLength: 3,
    maxLength: 160,
    minScore: 6,
  });

  const personalName = sanitizeNameValue(cleanFactValue([title, firstName, surname].filter(Boolean).join(" ")));
  const resolvedName = sanitizeNameValue(personalName || strictName || fullName || "");
  const uniqueCompany =
    company &&
    resolvedName &&
    (resolvedName.toLowerCase() === company.toLowerCase() ||
      resolvedName.toLowerCase().includes(company.toLowerCase()))
      ? ""
      : company;

  if (resolvedName && uniqueCompany) return `${resolvedName} (${uniqueCompany})`;
  return resolvedName || uniqueCompany || "";
};

const findSubmittedBy = (fields, agentName, applicantName) => {
  const strict = findStrictFactValue(
    fields,
    (field) =>
      /are you an applicant or an agent|submitted by|acting as/i.test(
        String(field?.question_contextual || field?.question || field?.label || "")
      ) ||
      /applicant_or_agent|submitted_by|acting_as/.test(String(field?.field_key || field?.key || "").toLowerCase())
  );
  const fuzzy = strict
    ? strict
    : findBestFactValue(fields, {
        phrases: ["submitted by", "acting as", "applicant or agent"],
        tokens: ["applicant", "agent", "submitted", "acting"],
        preferShortText: true,
        minLength: 3,
        maxLength: 40,
        minScore: 6,
      });
  const normalized = normalizeSubmittedBy(fuzzy);
  if (normalized) return normalized;
  return agentName ? "Agent" : applicantName ? "Applicant" : "";
};

const findProposalText = (fields) => {
  const strict = findStrictFactValue(
    fields,
    (field) =>
      /description of proposal|please describe accurately the work proposed|describe the proposal|proposal including|description of development|development description|variation sought|what is the variation/i.test(
        String(field?.question_contextual || field?.question || field?.label || "")
      ) ||
      /(?:^|\.)(proposal|description_of_proposal|description_of_development|development_description|work_proposed|variation_sought)$/.test(
        String(field?.field_key || field?.key || "").toLowerCase()
      )
  );
  if (strict) return strict;

  return findBestFactValue(fields, {
    groupIds: ["proposal", "development"],
    phrases: [
      "description of proposal",
      "describe the proposal",
      "work proposed",
      "description of development",
      "variation sought",
      "proposed development",
    ],
    tokens: [
      "proposal",
      "proposed",
      "work",
      "works",
      "development",
      "description",
      "variation",
      "amendment",
      "scheme",
    ],
    excludeTokens: ["fee", "payment", "email", "phone", "postcode", "reference", "application type"],
    preferLongText: true,
    minLength: 12,
    minScore: 7,
  });
};

const findWorkStarted = (fields) => {
  const strict = findStrictFactValue(
    fields,
    (field) =>
      /work already been started|has the work started|started and\/or completed|development commenced|has development begun|has implementation begun|commencement/i.test(
        String(field?.question_contextual || field?.question || field?.label || "")
      ) ||
      /work_already_started_or_completed|work_started|development_started|commencement/.test(
        String(field?.field_key || field?.key || "").toLowerCase()
      )
  );
  if (strict) return normalizeBooleanish(strict);

  const fuzzy = findBestFactValue(fields, {
    groupIds: ["proposal", "dates", "development"],
    phrases: [
      "has the work started",
      "work already started",
      "started and completed",
      "has development commenced",
      "commencement date",
    ],
    tokens: ["work", "started", "commenced", "commencement", "begun", "completed", "implemented", "implementation"],
    excludeTokens: ["received", "validated", "decision", "expiry"],
    preferBoolean: true,
    minLength: 2,
    maxLength: 60,
    minScore: 6,
  });
  return normalizeBooleanish(fuzzy);
};

export const buildApplicationInsights = (fields) => {
  const agentName = composePersonName(fields, "agent_details");
  const applicantName = composePersonName(fields, "applicant_details");
  const submittedBy = findSubmittedBy(fields, agentName, applicantName);
  const proposal = findProposalText(fields);
  const workStarted = findWorkStarted(fields);

  return [
    { label: "Agent Name", value: truncateFactValue(agentName) || "Not found" },
    { label: "Applicant Name", value: truncateFactValue(applicantName) || "Not found" },
    { label: "Submitted By", value: truncateFactValue(submittedBy) || "Not found" },
    { label: "Proposal", value: truncateFactValue(proposal, 180) || "Not found" },
    { label: "Work Started", value: truncateFactValue(workStarted) || "Not found" },
  ];
};

export const buildContactCard = (fields, contextId, roleLabel) => {
  const emailCandidate = findContextValue(fields, contextId, /\bemail\b/, /\.email/, {
    tokens: ["email", roleLabel],
    requireAnyHintTokens: ["email"],
    requiredType: "email",
    preferEmail: true,
    minScore: 4,
  });
  const phoneCandidate = findContextValue(
    fields,
    contextId,
    /\btelephone\b|\bphone\b|\bmobile\b|\btel\b/,
    /\.(telephone|telephone_number|phone|mobile|mobile_number|tel)$/,
    {
      tokens: ["telephone", "phone", "mobile", "tel", roleLabel],
      requireAnyHintTokens: ["telephone", "phone", "mobile", "tel"],
      requiredType: "phone",
      preferPhone: true,
      minScore: 4,
    }
  );
  const company = findContextValue(
    fields,
    contextId,
    /\bcompany\b|\borganisation\b|\borganization\b|\borg\b|corporate entity/,
    /\.(company|company_organisation|organisation|organization|corporate_entity|org)$/,
    {
      tokens: ["company", "organisation", "organization", "org", "corporate", "entity"],
      requireAnyHintTokens: ["company", "organisation", "organization", "org", "corporate", "entity"],
      excludeTokens: ["postcode", "post code", "address", "street", "town", "city", "email", "phone", "telephone"],
      minScore: 4,
    }
  );
  const email = extractEmailValue(emailCandidate);
  const phone = extractPhoneValue(phoneCandidate);
  const addressLine = findContextValue(fields, contextId, /\baddress\s*1\b|\bstreet\b/, /(?:^|[._])address(?:_?1)?$/, {
    tokens: ["address", "street", roleLabel],
    requireAnyHintTokens: ["address", "street", "line 1", "address line"],
    excludeTokens: ["town", "city", "postcode", "post code", "email", "phone"],
    minScore: 4,
  });
  const town = findContextValue(
    fields,
    contextId,
    /\btown\b|\bcity\b|\bsettlement\b/,
    /(?:^|[._])(town|city|town_city|town_city_settlement)$/,
    {
      tokens: ["town", "city", "settlement"],
      requireAnyHintTokens: ["town", "city", "settlement"],
      excludeTokens: ["postcode", "post code", "address", "street", "email", "phone"],
      minScore: 4,
    }
  );
  const postcode = findContextValue(
    fields,
    contextId,
    /\bpost\s*code\b|\bpostcode\b/,
    /(?:^|[._])(post_code|postcode)$/,
    {
      tokens: ["postcode", "post code"],
      requireAnyHintTokens: ["postcode", "post code"],
      excludeTokens: ["town", "city", "address", "street", "email", "phone"],
      minScore: 4,
    }
  );

  const name = composePersonName(fields, contextId) || company || "Not found";
  const location = joinDistinctFactParts([addressLine, town, postcode]) || "Not found";

  const availableCount = [name !== "Not found", Boolean(company), Boolean(email), Boolean(phone)].filter(Boolean).length;

  return {
    role: roleLabel,
    name,
    company: company || "Not found",
    email: email || "Not found",
    phone: phone || "Not found",
    location,
    availableCount,
  };
};

export const buildApplicationSignals = (fields, application, contacts = null) => {
  const agentName = contacts?.agent?.name && contacts.agent.name !== "Not found"
    ? contacts.agent.name
    : composePersonName(fields, "agent_details");
  const applicantName = contacts?.applicant?.name && contacts.applicant.name !== "Not found"
    ? contacts.applicant.name
    : composePersonName(fields, "applicant_details");

  const submittedBy = findSubmittedBy(fields, agentName, applicantName);
  const proposal = findProposalText(fields);
  const workStarted = findWorkStarted(fields);

  const siteAddress1 = findContextValue(fields, "site_address", /\baddress\s*1\b|\bstreet\b/, /(?:^|[._])address(?:_?1)?$/, {
    tokens: ["address", "street", "address 1", "line 1"],
    requireAnyHintTokens: ["address", "street", "line 1", "address 1"],
    excludeTokens: ["proposal", "development", "work", "variation", "scheme", "description"],
    minScore: 4,
  });
  const siteTown = findContextValue(
    fields,
    "site_address",
    /\btown\b|\bcity\b|\bsettlement\b/,
    /(?:^|[._])(town|city|town_city|town_city_settlement)$/,
    {
      tokens: ["town", "city", "settlement", "locality"],
      requireAnyHintTokens: ["town", "city", "settlement", "locality"],
      excludeTokens: ["proposal", "development", "work", "variation", "scheme", "description"],
      minScore: 4,
    }
  );
  const sitePostcode = findContextValue(
    fields,
    "site_address",
    /\bpost\s*code\b|\bpostcode\b/,
    /(?:^|[._])(post_code|postcode)$/,
    {
      tokens: ["postcode", "post code", "zip"],
      requireAnyHintTokens: ["postcode", "post code", "zip"],
      excludeTokens: ["proposal", "development", "work", "variation", "scheme", "description"],
      minScore: 4,
    }
  );
  const fallbackSite = sanitizeSiteFallback(application?.address || application?.location || "", proposal);
  const siteSummary = joinDistinctFactParts([siteAddress1, siteTown, sitePostcode]) || fallbackSite;

  return [
    {
      id: "submitted-by",
      label: "Submitted By",
      value: submittedBy || "Not found",
      sectionId: submittedBy
        ? (submittedBy.toLowerCase().includes("agent") ? "agent" : "applicant")
        : null,
    },
    {
      id: "work-started",
      label: "Work Started",
      value: workStarted || "Not found",
      sectionId: "proposal",
    },
    {
      id: "site-location",
      label: "Site Location",
      value: siteSummary || "Not found",
      sectionId: "site",
    },
    {
      id: "proposal",
      label: "Proposal",
      value: truncateFactValue(proposal, 260) || "Not found",
      sectionId: "proposal",
      long: true,
    },
  ];
};
